import React, { useState } from "react";

import { StatusChip, ChooseAddressDialog } from "@akj-dev/shared-components";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import PublicIcon from "@mui/icons-material/Public";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { Chip, Theme, useTheme } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import {
  NEW_PRODUCT,
  SAME_PRODUCT_WITH_CHANGES,
} from "../../../../../store/wlrBroadband/constants";
import { NoAddressErrorAmend } from "./NoAddressErrorAmend";
import { hasLocationAddress } from "../../../../../shared/utils/addresses";
import {
  WLR3Address,
  WLR3AddressSearch,
} from "../../../../../api/v1/addressSearch";

interface BroadbandProductSelectorProps {
  configuration: Record<string, any>;
  productInstance: Record<string, any>;
  location: Record<string, any>;
  updateProductInstance: Record<string, any>;
  lineAvailability: Record<string, any>;
  broadbandSearch: Record<string, any>;
  resignProduct: Record<string, any>;
  tagsCheck: Record<string, any>;
  setResignProduct: (
    productInstance: Record<string, any>,
    resignProduct: Record<string, any>,
    resignProductType: string
  ) => void;
  onSetAddress: (address: WLR3Address) => void;
}

export const BroadbandProductSelector = ({
  configuration,
  productInstance,
  tagsCheck,
  updateProductInstance,
  location,
  broadbandSearch,
  lineAvailability,
  resignProduct,
  setResignProduct,
  onSetAddress,
}: BroadbandProductSelectorProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (tagsCheck.fetching)
    return (
      <TableRow>
        <TableCell colSpan={9}>
          <StatusChip
            type="loading"
            title="Please wait"
            message="Checking Tags"
          />
        </TableCell>
      </TableRow>
    );
  if (tagsCheck.response.status === "error")
    return (
      <TableRow>
        <TableCell colSpan={9}>
          <StatusChip
            type="error"
            title="Error"
            message={tagsCheck.response.message}
          />
        </TableCell>
      </TableRow>
    );
  if (updateProductInstance.fetching)
    return (
      <TableRow>
        <TableCell colSpan={9}>
          <StatusChip
            type="loading"
            title="Please wait"
            message="Updating Product Instance"
          />
        </TableCell>
      </TableRow>
    );
  if (updateProductInstance.response.status === "error")
    return (
      <TableRow>
        <TableCell colSpan={9}>
          <StatusChip
            type="error"
            title="Error"
            message={updateProductInstance.response.message}
          />
        </TableCell>
      </TableRow>
    );
  // This field is required to be able to select an address.
  const hasPostcode = hasLocationAddress({ location, isPostcodeOnly: true });
  // These are the fields required to be able to do a line availability and broadband search.
  if (!hasPostcode) return <NoAddressErrorAmend onSetAddress={onSetAddress} />;
  if (!hasLocationAddress({ location })) {
    const { address } = location;
    return (
      <>
        <TableRow>
          <TableCell colSpan={9}>
            <StatusChip
              type="error"
              title="Error"
              message="This product instance doesn't have a full address. Please select an address to continue with
                the resign process."
              link={
                <div
                  className={classes.action}
                  onClick={() => setIsDialogOpen(true)}
                >
                  Select an address
                </div>
              }
            />
          </TableCell>
        </TableRow>
        <ChooseAddressDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          postcode={address.postcode}
          setAddress={onSetAddress}
          api={WLR3AddressSearch}
          //@TODO - account undefined in original component?
          //accountId={account}
        />
      </>
    );
  }

  // Only show SoGEA, FTTP or SoFast upgrades for ADSL / FTTC resigns
  const upgradeRegex = /FTTC|.MPF|ADSL/;
  const isEligibleForUpgrade = upgradeRegex.test(
    productInstance.broadband.technology_type
  );
  let products = broadbandSearch?.response?.products?.filter(
    (product: any) => !upgradeRegex.test(product.first_broadband_component.type)
  );

  // If no upgrades available, allow ADSL / FTTC resigns
  if (products && !products.length)
    products = broadbandSearch?.response?.products;

  return (
    <TableRow>
      <TableCell colSpan={9}>
        <div className={classes.outer}>
          <div style={{ flex: 1 }}>
            <div className={classes.headerCtr}>
              <AutorenewIcon style={{ color: "#fff", marginRight: 10 }} />
              <b style={{ color: "#fff" }}>Select product</b>
            </div>
            <Table style={{ backgroundColor: "#fff" }}>
              <TableBody>
                {resignProduct &&
                  configuration.resignProductType ===
                    SAME_PRODUCT_WITH_CHANGES && (
                    <TableRow data-cy="wlrBroadbandResignBBSameRow">
                      <TableCell>
                        <div className={classes.productCtr}>
                          <PublicIcon
                            style={{
                              color: theme.palette.grey[500],
                              marginRight: 10,
                            }}
                          />
                          <div style={{ fontWeight: "bold" }}>
                            {resignProduct.name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{resignProduct.description}</TableCell>
                      <TableCell>
                        &pound;
                        {
                          resignProduct.price
                            .first_bill_recurring_price_with_promotions
                        }
                      </TableCell>
                      <TableCell>
                        <div style={{ float: "right" }}>
                          <Checkbox
                            color="secondary"
                            onChange={() =>
                              setResignProduct(
                                productInstance,
                                resignProduct,
                                SAME_PRODUCT_WITH_CHANGES
                              )
                            }
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                {configuration.resignProductType === NEW_PRODUCT &&
                  lineAvailability.response.status === "success" &&
                  products &&
                  products.length > 0 &&
                  products.map((product: any) => {
                    return (
                      <TableRow
                        key={product.id}
                        data-cy="wlrBroadbandResignBBNewRow"
                      >
                        <TableCell>
                          <div className={classes.productCtr}>
                            <PublicIcon
                              sx={{
                                color: theme.palette.grey[500],
                                marginRight: 1,
                              }}
                            />
                            <div style={{ fontWeight: "bold" }}>
                              {product.name}
                            </div>
                            {isEligibleForUpgrade && (
                              <Chip
                                sx={{ marginLeft: 1 }}
                                label={`${product.first_broadband_component.type} Upgrade`}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{product.description}</TableCell>
                        <TableCell>
                          &pound;
                          {
                            product.price
                              .first_bill_recurring_price_with_promotions
                          }
                        </TableCell>
                        <TableCell>
                          <div style={{ float: "right" }}>
                            <Checkbox
                              color="secondary"
                              onChange={() =>
                                setResignProduct(
                                  productInstance,
                                  product,
                                  NEW_PRODUCT
                                )
                              }
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </div>
        {lineAvailability.fetching && (
          <StatusChip
            type="loading"
            title="Please wait"
            message="Checking Line & Broadband Availability"
          />
        )}
        {lineAvailability.response.status === "error" && (
          <StatusChip
            type="error"
            title="Error"
            message={lineAvailability.response.message}
          />
        )}
        {broadbandSearch.fetching && (
          <StatusChip
            type="loading"
            title="Please wait"
            message="Searching for Broadband Products"
          />
        )}
        {broadbandSearch.response.status === "error" && (
          <StatusChip
            type="error"
            title="Error"
            message={broadbandSearch.response.message}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  outer: {
    backgroundColor: theme.palette.grey[500],
    display: "flex",
    marginBottom: 10,
    marginTop: 10,
    padding: 10,
  },
  productCtr: {
    alignItems: "center",
    display: "flex",
    flex: 1,
  },
  headerCtr: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    paddingBottom: 16,
  },
  action: {
    marginLeft: 10,
    color: "inherit",
    fontWeight: "bold",
    textDecoration: "underline",
    cursor: "pointer",
  },
}));
